import { Attribute, Measure, useModelService } from 'services/model/model.service';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useLocation, useNavigate } from 'react-router-dom';
import { IntlShape, useIntl } from 'react-intl';
import { useContext } from 'react';
import { TenantConfig, TenantConfigContext } from 'context/TenantConfigContext';
import { useAppStore } from 'hooks/state/useAppStore';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { createArticleImageCell, createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';

interface TopSellingProductsRow {
  image: string;
  prdNumber: string;
  prdName: string;
  prdCategory: string;
  netSlsQty: number;
  netSlsQtyCompare: number;
  netSlsGmv: number;
  netSlsGmvCompare: number;
  avgNetSlsGmv: number;
  avgNetSlsGmvCompare: number;
  retRate: number;
  retRateCompare: number;
}

const columnHelper = createColumnHelper<TopSellingProductsRow>();
const buildColumns = (
  attributes: Attribute[],
  measures: Measure[],
  intl: IntlShape,
  tenantContext: TenantConfig,
  comparisonActive: boolean
) => [
  columnHelper.accessor('image', {
    enableSorting: false,
    header: () => intl.formatMessage({ id: 'general.label.image' }),
    cell: context => createArticleImageCell(context, tenantContext),
  }),
  columnHelper.accessor('prdNumber', {
    header: () => attributes[1].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor('prdName', {
    header: () => attributes[2].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor('prdCategory', {
    header: () => attributes[3].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor(row => ({ value: row.netSlsQty, compareValue: row.netSlsQtyCompare }), {
    id: measures[0].code,
    header: () => measures[0].metric.name,
    cell: context => createMeasureCell(context, measures[0], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.netSlsGmv, compareValue: row.netSlsGmvCompare }), {
    id: measures[1].code,
    header: () => measures[1].metric.name,
    cell: context => createMeasureCell(context, measures[1], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.avgNetSlsGmv, compareValue: row.avgNetSlsGmvCompare }), {
    id: measures[2].code,
    header: () => measures[2].metric.name,
    cell: context => createMeasureCell(context, measures[2], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retRate, compareValue: row.retRateCompare }), {
    id: measures[3].code,
    header: () => measures[3].metric.name,
    cell: context => createMeasureCell(context, measures[3], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
];

export function TopSellingProductsTable() {
  const intl = useIntl();
  const tenantContext = useContext(TenantConfigContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getMeasuresByMetrics, getAttributes, getMeasures } = useModelService();

  const attributes = getAttributes(['artImageUrl', 'prdNumber', 'prdName', 'prdCategory']);

  const [artImageUrl, prdNumber, prdName, prdCategory] = attributes;

  const measures = getMeasuresByMetrics(['netSlsQty', 'netSlsGmvVal', 'itemNetSlsGmvVal', 'retQtyRate'], 'OD');

  const [netSlsQty, netSlsGmvVal, itemNetSlsGmvVal, retQtyRate] = getMeasures([
    'netSlsQtyExpOD',
    'netSlsGmvValExpOD',
    'itemNetSlsGmvValExpOD',
    'retQtyRateExpOD',
  ]);

  const { fetchData } = useProDataService(Service.Products);
  const requestData = useDataRequest();

  const { products } = useAppStore();
  const productNumbers = products?.map(p => p);

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData(`reports/top-sellers/${netSlsQty.code}`, {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );
      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-products-top-selling-report']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const rows =
    reportState.data?.response.sets.products.map(row => {
      const productRow: TopSellingProductsRow = {
        image: artImageUrl.getValue(row.attributes),
        prdNumber: prdNumber.getValue(row.attributes),
        prdName: prdName.getValue(row.attributes),
        prdCategory: prdCategory.getValue(row.attributes),
        netSlsQty: netSlsQty.getValue(row.values),
        netSlsQtyCompare: netSlsQty.getValue(row.compareValues),
        netSlsGmv: netSlsGmvVal.getValue(row.values),
        netSlsGmvCompare: netSlsGmvVal.getValue(row.compareValues),
        avgNetSlsGmv: itemNetSlsGmvVal.getValue(row.values),
        avgNetSlsGmvCompare: itemNetSlsGmvVal.getValue(row.compareValues),
        retRate: retQtyRate.getValue(row.values),
        retRateCompare: retQtyRate.getValue(row.compareValues),
      };

      return productRow;
    }) ?? [];

  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  const columns = buildColumns(
    attributes,
    measures,
    intl,
    tenantContext,
    isComparisonActive
  ) as ColumnDef<TopSellingProductsRow>[];

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Products,
      measuresGroup: 'topSeller',
      tableName: 'Product Topseller',
      isProReport: true,
    },
  };

  const navigateToProductDetails = (productNumber: string) => {
    const url = `/product/${encodeURIComponent(productNumber)}${location.search}`;
    navigate(url);
  };

  const onRowClick = (row: Row<TopSellingProductsRow>) => {
    const prdNumberValue = row.getValue(prdNumber.code);
    navigateToProductDetails(prdNumberValue as string);
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.product.reports.top_selling.description"
        rows={rows}
        columns={columns}
        exportConfigOptions={exportConfigOptions}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        onRowClick={onRowClick}
        isError={reportState.isError}
        errorMessage={reportState.error}
      />
    </DataTableContextProvider>
  );
}
